import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import BrandLogo from "@/components/BrandLogo";
import Avatar from "@/components/Avatar";
import { BadgeGrid } from "@/components/Badges";
import { Users, LogOut, MessageSquare, User as UserIcon, Target, Trophy, TrendingUp } from "lucide-react";
import MobileNav from "@/components/MobileNav";

export default function ParentDashboard() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [perf, setPerf] = useState(null);

    useEffect(() => {
        api.get("/parent/child").then(r => setData(r.data)).catch(() => {});
        api.get("/parent/performance").then(r => setPerf(r.data)).catch(() => {});
    }, []);

    const overall = perf ? Math.round(((perf.my_quantitative_avg || 0) + (perf.my_verbal_avg || 0)) / 2) : 0;

    return (
        <div dir="rtl" className="min-h-screen bg-slate-50">
            <header className="bg-[#0F172A] text-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-2">
                    <BrandLogo size={40} />
                    <MobileNav
                        title="القائمة"
                        items={[
                            { label: "حسابي", icon: UserIcon, onClick: () => nav("/profile"), testid: "btn-profile" },
                            { label: "الرسائل", icon: MessageSquare, onClick: () => nav("/messages"), testid: "btn-open-messages" },
                            { label: "صفحة أداء ابنتي", icon: Target, onClick: () => nav("/parent/performance"), testid: "btn-open-performance" },
                            { label: "لوحة الشرف", icon: Trophy, onClick: () => nav("/parent/leaderboard"), testid: "btn-open-leaderboard" },
                            { label: "تسجيل الخروج", icon: LogOut, onClick: async () => { await logout(); nav("/login"); }, destructive: true, testid: "btn-logout" },
                        ]}
                    />
                </div>
            </header>
            <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
                <div className="bg-gradient-to-l from-[#1B2A4E] to-[#0F172A] text-white rounded-2xl p-8">
                    <div className="flex items-center gap-3 mb-2"><Users className="w-6 h-6 text-[#C8102E]" /><span className="text-xs text-white/70 uppercase tracking-wider">لوحة ولي الأمر</span></div>
                    <h1 className="text-2xl font-bold">أهلاً {user?.full_name}</h1>
                    <p className="text-white/70 mt-1 text-sm">متابعة أداء ابنتك في اختبار القدرات</p>
                </div>

                {data?.student && (
                    <div className="bg-white rounded-2xl border border-slate-200 p-6">
                        <div className="flex items-center gap-4">
                            <Avatar name={data.student.full_name} seed={data.student.full_name} role="student" size={72} />
                            <div className="flex-1">
                                <div className="text-xs text-slate-500 mb-1">الطالبة</div>
                                <div className="text-2xl font-bold text-[#0F172A]">{data.student.full_name}</div>
                                <div className="text-sm text-slate-600 mt-1">{data.class?.name}</div>
                            </div>
                            <Button onClick={() => nav("/parent/performance")} data-testid="btn-child-performance" className="bg-[#1B2A4E] hover:bg-[#0F172A]">
                                <Target className="w-4 h-4 ml-1" /> صفحة الأداء الكاملة
                            </Button>
                        </div>
                    </div>
                )}

                {perf && (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <button onClick={() => nav("/parent/performance")} className="bg-white rounded-xl border border-slate-200 p-5 text-right hover:shadow-md transition-all">
                                <div className="w-10 h-10 rounded-lg bg-slate-100 text-[#1B2A4E] flex items-center justify-center mb-3"><Target className="w-5 h-5" /></div>
                                <div className="text-3xl font-bold text-[#0F172A] font-mono-num">{overall}%</div>
                                <div className="text-xs text-slate-500 mt-1">المتوسط العام</div>
                            </button>
                            <button onClick={() => nav("/parent/performance")} className="bg-white rounded-xl border border-slate-200 p-5 text-right hover:shadow-md transition-all">
                                <div className="w-10 h-10 rounded-lg bg-slate-100 text-[#1B2A4E] flex items-center justify-center mb-3"><Trophy className="w-5 h-5" /></div>
                                <div className="text-3xl font-bold text-[#0F172A] font-mono-num">{perf.my_quantitative_avg}%</div>
                                <div className="text-xs text-slate-500 mt-1">قدرات كمية</div>
                            </button>
                            <button onClick={() => nav("/parent/performance")} className="bg-white rounded-xl border border-slate-200 p-5 text-right hover:shadow-md transition-all">
                                <div className="w-10 h-10 rounded-lg bg-[#FFF1F2] text-[#C8102E] flex items-center justify-center mb-3"><Trophy className="w-5 h-5" /></div>
                                <div className="text-3xl font-bold text-[#0F172A] font-mono-num">{perf.my_verbal_avg}%</div>
                                <div className="text-xs text-slate-500 mt-1">قدرات لفظية</div>
                            </button>
                            <button onClick={() => nav("/parent/leaderboard")} data-testid="btn-parent-leaderboard" className="bg-white rounded-xl border border-slate-200 p-5 text-right hover:shadow-md transition-all">
                                <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center mb-3"><TrendingUp className="w-5 h-5" /></div>
                                <div className="text-3xl font-bold text-[#0F172A] font-mono-num">{perf.my_total_attempts}</div>
                                <div className="text-xs text-slate-500 mt-1">اختبارات محلولة</div>
                            </button>
                        </div>

                        <div className="bg-white rounded-2xl border border-slate-200 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="font-semibold text-[#0F172A]">شارات ابنتكِ</div>
                                <Badge variant="outline">{(perf.badges || []).length} شارة</Badge>
                            </div>
                            <BadgeGrid badges={perf.badges || []} emptyText="لم تحصل ابنتكِ على شارات بعد" />
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}
